import { chain } from 'lodash';
import { RouteHelper } from '../../helpers/route.helper';
import type { TelematicsDelegation } from '../../interfaces/telematics/telematics-delegation';
import type { TelematicsEmptyDistance } from '../../interfaces/telematics/telematics-empty-distance';
import type { TelematicsOrder } from '../../interfaces/telematics/telematics-order';
import type { TelematicsRoutePoint } from '../../interfaces/telematics/telematics-route-point';

export class TelematicsEmptyDistanceFactory {
  public static from(data: {
    routePoints: TelematicsRoutePoint[];
    orders: TelematicsOrder[];
    delegations: TelematicsDelegation[];
  }): TelematicsEmptyDistance[] {
    const emptyDistances: TelematicsEmptyDistance[] = [];
    let lastOrder: TelematicsOrder | null = null;

    for (const order of data.orders) {
      const points = order.points.filter(
        (point) => !!point && !RouteHelper.isSwapPoint(point),
      );
      const lastEmptyIndex = points.findLastIndex((point) =>
        RouteHelper.isEmpty(
          data.routePoints,
          data.routePoints[RouteHelper.getIndex(data.routePoints, point)],
        ),
      );
      if (lastEmptyIndex === -1) {
        lastOrder = order;
        continue;
      }

      const delegationStart = data.delegations.findLast(
        (delegation) => delegation.start <= order.start,
      )?.start;
      const lastOrderEnd = lastOrder?.end;

      const emptyPoints = points.slice(0, lastEmptyIndex + 1);
      const { distance, duration } = emptyPoints.reduce(
        (result, point) => {
          const { distance, duration } = point.route.summary;
          return {
            distance: result.distance + distance,
            duration: result.duration + duration,
          };
        },
        { distance: 0, duration: 0 },
      );

      emptyDistances.push({
        start:
          chain([delegationStart, lastOrderEnd]).compact().max().value() ??
          order.start.minus({ seconds: duration }),
        end: order.start,
        key: order.key,
        orderUuid: order.uuid,
        distance,
        percentage:
          (distance /
            (distance + (order.order?.route?.summary?.distance ?? 0))) *
          100,
        duration,
      });

      lastOrder = order;
    }

    return emptyDistances;
  }
}
